// Iter-85 — Editorial data sources client. Single source of truth for
// every public-facing data claim (savings figures, national averages,
// methodology anchors). Backed by `/api/data-sources`.
//
// Public consumers use the `useDataField(key)` hook + render the
// resulting value alongside the inline `<DataAttribution />` component
// in `components/DataAttribution.jsx`.
//
// Caching: the public registry is small (<10 keys today, ~20 max) and
// rarely changes. We fetch once on mount and cache for the page
// lifetime. Admin edits don't propagate to live tabs until refresh —
// fine for this scale.
import { useEffect, useState } from "react";
import { API } from "./api";

let _cache = null;
let _inflight = null;

export async function loadDataSources() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${API}/data-sources`, { credentials: "omit" });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      _cache = data.items_by_key || {};
      return _cache;
    } catch (e) {
      // Don't poison the cache on failure — let the next caller retry.
      // eslint-disable-next-line no-console
      console.warn("[carsavr] data-sources fetch failed", e);
      return {};
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * useDataField(key)
 * Returns { field, ready } where field is:
 *   { key, label, value, source_name, source_url, source_year,
 *     last_reviewed_at, disclaimer }
 * or null if the key isn't registered yet.
 *
 * Component should render a safe fallback while `ready` is false.
 */
export function useDataField(key) {
  const [state, setState] = useState({ field: null, ready: false });
  useEffect(() => {
    let cancelled = false;
    loadDataSources().then((items) => {
      if (cancelled) return;
      setState({ field: items[key] || null, ready: true });
    });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return state;
}

// For the methodology page — return the full registry sorted by key.
export async function listAllDataFields() {
  const items = await loadDataSources();
  return Object.values(items).sort((a, b) =>
    (a.key || "").localeCompare(b.key || ""),
  );
}

/**
 * useGlobalLastReviewed()
 * Convenience hook returning the global editorial review date from the
 * `global.editorial_last_reviewed` registry key. Falls back to a static
 * default if the registry hasn't hydrated yet (prevents layout jumps).
 *
 * Usage in state/city/review pages: replaces hardcoded date constants
 * so a single admin edit propagates everywhere.
 */
export function useGlobalLastReviewed(fallback = "2026-06-29") {
  const { field, ready } = useDataField("global.editorial_last_reviewed");
  if (!ready) return fallback;
  return field?.last_reviewed_at || fallback;
}

/**
 * useCalculatorAssumption(key, fallback)
 * Iter-253 — Returns a numeric assumption (multiplier, weight, cap)
 * from the editorial data_sources registry as a parsed `Number`,
 * falling back to the hardcoded default if the registry hasn't
 * hydrated, the key is missing, or the stored value is non-numeric.
 *
 * Used by the Tier-2 calculators (FirstAccidentImpactCalculator,
 * TelematicsScorePredictor) so the editorial team can refresh the
 * underlying numbers when 2026 carrier filings publish — without
 * a frontend deploy.
 *
 * Usage:
 *   const minor = useCalculatorAssumption("calc.first_accident.severity_minor_mult", 1.22);
 */
export function useCalculatorAssumption(key, fallback) {
  const { field, ready } = useDataField(key);
  if (!ready) return fallback;
  const raw = field?.value;
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}
